import * as React from 'react'
import { ArrowDown, ArrowUp, Check, ExternalLink, Loader2, Plus, Star, Trash2, TriangleAlert } from 'lucide-react'
import { REVIEW_LINK_NAME_MAX, REVIEW_LINKS_MAX } from '@lib/constants/couple-feedback'
import type { ReviewLinkSuggestionDto, VenueReviewLinkDto, VenueReviewLinkSettingDto } from '@lib/dto/couple-feedback.dto'
import { reviewLinkProblem, reviewLinkWarnings, reviewSiteName } from '@lib/helpers/review-sites'
import { putVenueReviewLinksInputSchema } from '@lib/input/couple-feedback.input'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useReplaceVenueReviewLinks, useReviewLinkSuggestions, useVenueReviewLinks } from '@/hooks/useCoupleFeedback'
import { reviewLinkHint } from '@/lib/review-link-hints'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { FormError } from '@/components/ui/form-error'
import { Input } from '@/components/ui/input'
import { EmptyState, ErrorState } from '@/components/ui/states'

interface Row {
  /** Keeps a row's inputs its own while rows around it move or go. */
  key: string
  name: string
  url: string
  /**
   * The name was typed rather than filled in from the address. Until it is,
   * pasting a Google, Yelp or The Knot address names the row for them.
   */
  named: boolean
}

type RowErrors = Record<string, { name?: string | undefined; url?: string | undefined } | undefined>

const NAMES = new Intl.ListFormat('en-US', { style: 'long', type: 'conjunction' })

function rowOf(link: VenueReviewLinkDto): Row {
  return { key: crypto.randomUUID(), name: link.name, url: link.url, named: link.name !== reviewSiteName(link.url) }
}

function emptyRow(): Row {
  return { key: crypto.randomUUID(), name: '', url: '', named: false }
}

/** A row the venue left blank isn't a place, and isn't an error either. */
function isBlank(row: Row): boolean {
  return row.name.trim() === '' && row.url.trim() === ''
}

/** Whether the typed address can be opened to check it. */
function opensAsTyped(url: string): boolean {
  if (!/^https:\/\/\S+$/i.test(url)) return false
  try {
    return new URL(url).protocol === 'https:'
  } catch {
    return false
  }
}

function sameAsSaved(rows: readonly Row[], saved: readonly VenueReviewLinkDto[]): boolean {
  const kept = rows.filter((row) => !isBlank(row))
  return kept.length === saved.length && kept.every((row, i) => row.name.trim() === saved[i]?.name && row.url.trim() === saved[i]?.url)
}

/** What a couple sees, for someone who can't change the list. */
function ReadOnlyLinks({ links }: { links: readonly VenueReviewLinkDto[] }) {
  if (links.length === 0) {
    return <p className="text-sm text-ink-muted">No review places yet. Couples can still send you a private note.</p>
  }
  return (
    <ol className="m-0 list-none space-y-2 p-0">
      {links.map((link) => (
        <li key={link.url} className="min-w-0 text-sm">
          <span className="font-medium text-ink">{link.name}</span>
          <span className="block wrap-anywhere text-small text-ink-muted">{link.url}</span>
        </li>
      ))}
    </ol>
  )
}

/**
 * The places the venue's GoHighLevel sub-accounts already know, ready to add:
 * built from the listing, so there is nothing to copy wrong. Each can be
 * opened first, to see whose listing it is.
 */
function Suggestions({
  suggestions,
  full,
  onAdd,
}: {
  suggestions: readonly ReviewLinkSuggestionDto[]
  full: boolean
  onAdd: (suggestion: ReviewLinkSuggestionDto) => void
}) {
  return (
    <Card className="space-y-3 p-4">
      <div className="space-y-0.5">
        <p className="m-0 text-heading font-semibold text-ink">From GoHighLevel</p>
        <p className="m-0 text-small text-ink-muted">Your sub-account already has these. Check each one opens your venue, then add it.</p>
      </div>
      <ul className="m-0 list-none space-y-2 p-0">
        {suggestions.map((suggestion) => (
          <li key={suggestion.url} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm font-medium text-ink">{suggestion.name}</span>
            <span className="min-w-0 text-small text-ink-muted">from {suggestion.from}</span>
            <a
              href={suggestion.checkUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-small text-accent-ink underline-offset-2 hover:underline"
            >
              Check it on {suggestion.name}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              disabled={full}
              aria-label={`Add ${suggestion.name} from ${suggestion.from}`}
              onClick={() => onAdd(suggestion)}
            >
              <Plus className="h-4 w-4" aria-hidden /> Add
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  )
}

/**
 * The venue's public review places, in the order the couple sees them, saved
 * as one list (like a vendor's video links). Blank rows are dropped on save.
 * An address copied from the wrong screen is refused as it is typed; anything
 * that may be wrong but can be right (a name that says another site, another
 * Google listing than GoHighLevel's, an address a sister venue offers) is a
 * warning under the row.
 */
function ReviewLinksEditor({
  venueId,
  saved,
  suggestions,
}: {
  venueId: string
  saved: readonly VenueReviewLinkSettingDto[]
  suggestions: readonly ReviewLinkSuggestionDto[]
}) {
  const replace = useReplaceVenueReviewLinks(venueId)
  const [rows, setRows] = React.useState<Row[]>(() => saved.map(rowOf))
  const [rowErrors, setRowErrors] = React.useState<RowErrors>({})
  const [listError, setListError] = React.useState<string | null>(null)
  const [justSaved, setJustSaved] = React.useState(false)
  const dirty = !sameAsSaved(rows, saved)
  const full = rows.length >= REVIEW_LINKS_MAX
  const googlePlaceIds = suggestions.flatMap((suggestion) => (suggestion.googlePlaceId === null ? [] : [suggestion.googlePlaceId]))
  const alsoOn = new Map(saved.map((link) => [link.url, link.alsoOn]))
  // Where to find a link is for a place being added or changed, not one already saved as it is.
  const savedAsIs = new Set(saved.map((link) => `${link.name}\n${link.url}`))
  const offered = suggestions.filter((suggestion) => !rows.some((row) => row.url.trim() === suggestion.url))

  function edited(key: string): void {
    setRowErrors((current) => ({ ...current, [key]: undefined }))
    setListError(null)
    setJustSaved(false)
  }

  function changeName(key: string, name: string): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, name, named: name.trim() !== '' } : row)))
    edited(key)
  }

  function changeUrl(key: string, url: string): void {
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row
        if (row.named) return { ...row, url }
        return { ...row, url, name: reviewSiteName(url.trim()) ?? '' }
      }),
    )
    edited(key)
  }

  function move(key: string, by: -1 | 1): void {
    setRows((current) => {
      const from = current.findIndex((row) => row.key === key)
      const moving = current[from]
      const to = from + by
      if (moving === undefined || to < 0 || to >= current.length) return current
      const next = current.filter((row) => row.key !== key)
      next.splice(to, 0, moving)
      return next
    })
    setJustSaved(false)
  }

  function remove(key: string): void {
    setRows((current) => current.filter((row) => row.key !== key))
    edited(key)
  }

  function add(): void {
    setRows((current) => [...current, emptyRow()])
    setJustSaved(false)
  }

  function addSuggestion(suggestion: ReviewLinkSuggestionDto): void {
    setRows((current) => {
      if (current.length >= REVIEW_LINKS_MAX || current.some((row) => row.url.trim() === suggestion.url)) return current
      // A blank row left over from "Add a review place" takes it, rather than sitting empty above it.
      const blank = current.findIndex(isBlank)
      const row = { key: crypto.randomUUID(), name: suggestion.name, url: suggestion.url, named: false }
      if (blank === -1) return [...current, row]
      return current.map((existing, i) => (i === blank ? row : existing))
    })
    setListError(null)
    setJustSaved(false)
  }

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    setListError(null)
    setJustSaved(false)
    const kept = rows.filter((row) => !isBlank(row))
    const parsed = putVenueReviewLinksInputSchema.safeParse({
      links: kept.map((row) => ({ name: row.name.trim(), url: row.url.trim() })),
    })
    if (!parsed.success) {
      const errors: RowErrors = {}
      let other: string | null = null
      for (const issue of parsed.error.issues) {
        const [, index, field] = issue.path
        const row = typeof index === 'number' ? kept[index] : undefined
        if (row !== undefined && (field === 'name' || field === 'url')) {
          errors[row.key] = { ...errors[row.key], [field]: errors[row.key]?.[field] ?? issue.message }
        } else {
          other ??= issue.message
        }
      }
      setRowErrors(errors)
      setListError(other)
      return
    }
    setRowErrors({})
    replace.mutate(parsed.data, {
      onSuccess: (result) => {
        setRows(result.links.map(rowOf))
        setJustSaved(true)
      },
      onError: (cause) => setListError(requestErrorMessage(cause)),
    })
  }

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      {offered.length > 0 ? <Suggestions suggestions={offered} full={full} onAdd={addSuggestion} /> : null}
      {rows.length === 0 ? (
        <EmptyState
          icon={Star}
          title="No review places yet"
          description="Couples can still send you a private note. Add a place to offer them a public review after they do."
          action={
            <Button type="button" variant="outline" onClick={add}>
              <Plus className="h-4 w-4" aria-hidden /> Add a review place
            </Button>
          }
        />
      ) : (
        <ol className="m-0 list-none space-y-3 p-0">
          {rows.map((row, index) => {
            const label = row.name.trim() === '' ? `review place ${String(index + 1)}` : row.name.trim()
            const errors = rowErrors[row.key]
            const url = row.url.trim()
            const problem = url === '' ? null : reviewLinkProblem(url)
            const warnings = url === '' || problem !== null ? [] : reviewLinkWarnings({ name: row.name, url }, { googlePlaceIds })
            const sisters = alsoOn.get(url) ?? []
            if (sisters.length > 0) {
              warnings.push(`${NAMES.format(sisters)} also ${sisters.length === 1 ? 'offers' : 'offer'} this address. Check it’s this venue’s.`)
            }
            return (
              <Card key={row.key} asChild className="p-4">
                <li className="grid gap-3 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] sm:items-start">
                  <Field label="Site" htmlFor={`review-name-${row.key}`} error={errors?.name}>
                    <Input
                      id={`review-name-${row.key}`}
                      value={row.name}
                      maxLength={REVIEW_LINK_NAME_MAX}
                      placeholder="Yelp"
                      onChange={(e) => changeName(row.key, e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Review address"
                    htmlFor={`review-url-${row.key}`}
                    hint={savedAsIs.has(`${row.name.trim()}\n${url}`) ? undefined : reviewLinkHint(row)}
                    error={errors?.url ?? problem}
                  >
                    <Input
                      id={`review-url-${row.key}`}
                      type="url"
                      inputMode="url"
                      value={row.url}
                      placeholder="https://…"
                      onChange={(e) => changeUrl(row.key, e.target.value)}
                    />
                  </Field>
                  <div className="flex items-center gap-1 sm:pt-6">
                    {opensAsTyped(url) ? (
                      <Button variant="ghost" size="icon" asChild>
                        <a href={url} target="_blank" rel="noreferrer" aria-label={`Open ${label} to check it`}>
                          <ExternalLink className="h-4 w-4" aria-hidden />
                        </a>
                      </Button>
                    ) : (
                      <Button type="button" variant="ghost" size="icon" disabled aria-label={`Open ${label} to check it`}>
                        <ExternalLink className="h-4 w-4" aria-hidden />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Move ${label} up`}
                      disabled={index === 0}
                      onClick={() => move(row.key, -1)}
                    >
                      <ArrowUp className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Move ${label} down`}
                      disabled={index === rows.length - 1}
                      onClick={() => move(row.key, 1)}
                    >
                      <ArrowDown className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Remove ${label}`} onClick={() => remove(row.key)}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                  {warnings.length > 0 ? (
                    <ul className="m-0 list-none space-y-1 p-0 sm:col-span-3">
                      {warnings.map((warning) => (
                        <li key={warning} className="flex items-start gap-1.5 text-small text-hold-ink">
                          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                          {warning}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              </Card>
            )
          })}
        </ol>
      )}
      {rows.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" size="sm" disabled={full} onClick={add}>
            <Plus className="h-4 w-4" aria-hidden /> Add a review place
          </Button>
          {full ? <span className="text-small text-ink-muted">Up to {REVIEW_LINKS_MAX} review places.</span> : null}
        </div>
      ) : null}
      {listError !== null ? <FormError>{listError}</FormError> : null}
      {dirty || justSaved || saved.length > 0 ? (
        <FormActions align="start">
          <Button type="submit" pending={replace.isPending} disabled={!dirty && !replace.isPending}>
            Save review places
          </Button>
          {dirty && !replace.isPending ? <span className="text-small text-ink-muted">Not saved yet.</span> : null}
          {justSaved && !dirty ? (
            <span className="inline-flex items-center gap-1 text-sm text-ok-ink">
              <Check className="h-4 w-4" aria-hidden /> Saved.
            </span>
          ) : null}
        </FormActions>
      ) : null}
    </form>
  )
}

/**
 * Where a couple can review this venue in public: Google, Facebook, Yelp, The
 * Knot, or any other site the venue names. Every one is offered once a couple
 * has sent their private note, whatever they said. With none, the note still
 * works; there is just nothing public to offer. A venue on GoHighLevel is
 * offered its sub-accounts' Google listing and Facebook page to add.
 */
export function VenueReviewLinksSection({
  venueId,
  canEdit,
}: {
  venueId: string
  /** Venue setup: Edit. */
  canEdit: boolean
}): React.ReactElement {
  const query = useVenueReviewLinks(venueId)
  const suggestions = useReviewLinkSuggestions(venueId, canEdit)

  if (query.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading review places…
      </p>
    )
  }
  if (query.isError) {
    return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  }

  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-sm text-ink-muted">
        After a couple sends you their private note, they see a button for each place below, in this order. Adding one says where to find
        its link on that site.
      </p>
      {canEdit ? (
        <ReviewLinksEditor venueId={venueId} saved={query.data.links} suggestions={suggestions.data?.suggestions ?? []} />
      ) : (
        <>
          <p className="text-sm text-ink-muted">Only someone who can edit Venue setup can change these.</p>
          <ReadOnlyLinks links={query.data.links} />
        </>
      )}
    </div>
  )
}
